import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, Download, CheckCircle2, AlertCircle, X, Pencil } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "@/hooks/useTranslation";
import { useOrganization } from "@/contexts/OrganizationContext";
import { ColumnMappingStep } from "./import/ColumnMappingStep";
import { autoMapColumns, getSchemaFields, ColumnMapping } from "@/lib/columnMapping";
import * as XLSX from "xlsx";

interface ParsedPlayer {
  external_ref?: string;
  first_name: string;
  last_name: string;
  date_of_birth?: string;
  position?: string;
  dominant_foot?: string;
  height_cm?: number;
  weight_kg?: number;
  jersey_number?: number;
  squad?: string;
  valid: boolean;
  errors: string[];
}

interface PlayerImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamId: string;
  onImportComplete: () => void;
}

/**
 * Normalises a date string from various common formats to ISO YYYY-MM-DD.
 * Handles: YYYY-MM-DD, DD/MM/YYYY, MM/DD/YYYY, DD-MM-YYYY, DD.MM.YYYY,
 * and Excel serial date numbers.
 */
function normalizeDateString(raw: string): string | undefined {
  const s = raw.trim();
  if (!s) return undefined;

  // Excel serial date (integer number like "32507")
  const serial = Number(s);
  if (!isNaN(serial) && /^\d+$/.test(s) && serial > 1000 && serial < 100000) {
    // Excel epoch: Dec 30, 1899
    const excelEpoch = new Date(1899, 11, 30);
    const d = new Date(excelEpoch.getTime() + serial * 86400000);
    if (!isNaN(d.getTime())) {
      return d.toISOString().slice(0, 10);
    }
  }

  // Already ISO: YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const d = new Date(s);
    return isNaN(d.getTime()) ? undefined : s;
  }

  // Separator-based: DD/MM/YYYY, MM/DD/YYYY, DD-MM-YYYY, DD.MM.YYYY
  const match = s.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{2,4})$/);
  if (match) {
    let [, a, b, y] = match;
    const year = y.length === 2 ? (parseInt(y) > 30 ? `19${y}` : `20${y}`) : y;
    const aNum = parseInt(a);
    const bNum = parseInt(b);
    let day: number, month: number;
    // If first part > 12 it must be the day (DD/MM/YYYY)
    if (aNum > 12) {
      day = aNum; month = bNum;
    } else {
      // Assume DD/MM/YYYY (European) as default — most sports clubs use this
      day = aNum; month = bNum;
    }
    if (month < 1 || month > 12 || day < 1 || day > 31) return undefined;
    const iso = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? undefined : iso;
  }

  // Try native Date parse as last resort (handles "28 Nov 1988", "Nov 28, 1988" etc.)
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    return d.toISOString().slice(0, 10);
  }

  return undefined;
}

const PLAYER_SCHEMA_FIELDS = getSchemaFields("players");
const PLAYER_FIELD_NAMES = PLAYER_SCHEMA_FIELDS.map(f => f.field);

export const PlayerImportDialog = ({ open, onOpenChange, teamId, onImportComplete }: PlayerImportDialogProps) => {
  const { toast } = useToast();
  const { translate } = useTranslation();
  const { currentOrganization } = useOrganization();
  const [file, setFile] = useState<File | null>(null);
  const [parsedPlayers, setParsedPlayers] = useState<ParsedPlayer[]>([]);
  const [mappedData, setMappedData] = useState<Record<string, any>[]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<Record<string, any>[]>([]);
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [step, setStep] = useState<"upload" | "mapping" | "preview">("upload");

  const downloadTemplate = () => {
    // Template uses separate first/last name columns — both formats are accepted
    const headers = ["first_name", "last_name", "squad", "position", "jersey_number", "date_of_birth", "height_cm", "weight_kg", "dominant_foot", "external_ref"];
    const sampleData = [
      ["John", "Smith", "Men's 1st Team", "Forward", "10", "1998-06-15", "182", "75", "right", ""],
      ["Sarah", "Johnson", "Women's 1st Team", "Midfielder", "8", "2000-03-22", "168", "62", "left", "SJ001"],
    ];

    const csvContent = [headers.join(","), ...sampleData.map(row => row.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "players_template.csv";
    a.click();
    window.URL.revokeObjectURL(url);
    toast({ title: translate("player_import_template_downloaded"), description: translate("player_import_template_desc") });
  };

  const validatePlayer = (player: Record<string, any>): ParsedPlayer => {
    const errors: string[] = [];
    let firstName = (player.first_name || "").toString().trim();
    let lastName = (player.last_name || "").toString().trim();

    // Handle combined name field
    if (!firstName && !lastName) {
      const nameField = (player.name || "").toString().trim();
      if (nameField) {
        const parts = nameField.split(/\s+/);
        firstName = parts[0] || "";
        lastName = parts.length >= 2 ? parts.slice(1).join(" ") : parts[0] || "";
      }
    }

    if (!firstName) errors.push("First name is required");
    if (!lastName) errors.push("Last name is required");

    // Normalize dominant foot
    let foot = (player.dominant_foot || "").toString().trim().toLowerCase();
    if (foot === "r") foot = "right";
    else if (foot === "l") foot = "left";
    else if (foot === "b") foot = "both";
    if (foot && !["left", "right", "both"].includes(foot)) {
      errors.push("Dominant foot must be 'left', 'right', or 'both'");
      foot = "";
    }

    const heightVal = player.height_cm ? parseFloat(player.height_cm) : undefined;
    const weightVal = player.weight_kg ? parseFloat(player.weight_kg) : undefined;
    const jerseyVal = player.jersey_number ? parseInt(player.jersey_number) : undefined;

    if (heightVal !== undefined && (isNaN(heightVal) || heightVal <= 0)) errors.push("Height must be a positive number");
    if (weightVal !== undefined && (isNaN(weightVal) || weightVal <= 0)) errors.push("Weight must be a positive number");
    if (jerseyVal !== undefined && (isNaN(jerseyVal) || jerseyVal < 1 || jerseyVal > 99)) errors.push("Jersey number must be between 1 and 99");

    // Handle age → DOB
    let dob: string | undefined = undefined;
    const rawDob = (player.date_of_birth || "").toString().trim();
    if (rawDob) {
      dob = normalizeDateString(rawDob);
      if (!dob) errors.push(`Unrecognised date format: "${rawDob}". Use YYYY-MM-DD, DD/MM/YYYY, or MM/DD/YYYY.`);
    } else if (player.age) {
      const age = parseInt(player.age);
      if (!isNaN(age) && age > 0 && age < 100) {
        dob = `${new Date().getFullYear() - age}-01-01`;
      }
    }

    return {
      external_ref: player.external_ref?.toString().trim() || undefined,
      first_name: firstName,
      last_name: lastName,
      date_of_birth: dob,
      position: player.position?.toString().trim() || undefined,
      dominant_foot: foot || undefined,
      height_cm: heightVal && !isNaN(heightVal) && heightVal > 0 ? heightVal : undefined,
      weight_kg: weightVal && !isNaN(weightVal) && weightVal > 0 ? weightVal : undefined,
      jersey_number: jerseyVal && !isNaN(jerseyVal) && jerseyVal >= 1 && jerseyVal <= 99 ? jerseyVal : undefined,
      squad: player.squad?.toString().trim() || undefined,
      valid: errors.length === 0,
      errors,
    };
  };

  const parseRowsFromFile = async (uploadedFile: File): Promise<{ headers: string[]; rows: Record<string, any>[] }> => {
    const isExcel = /\.(xlsx|xls)$/i.test(uploadedFile.name);
    if (isExcel) {
      const buffer = await uploadedFile.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: "" });
      if (jsonData.length === 0) throw new Error("The spreadsheet appears to be empty.");
      return { headers: Object.keys(jsonData[0]), rows: jsonData };
    } else {
      const text = await uploadedFile.text();
      const lines = text.split("\n").filter(line => line.trim());
      if (lines.length < 2) throw new Error("CSV must have at least a header row and one data row.");
      const headers = lines[0].split(",").map(h => h.trim());
      const rows: Record<string, any>[] = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(",").map(v => v.trim());
        const rowObj: Record<string, any> = {};
        headers.forEach((header, index) => { rowObj[header] = values[index] ?? ""; });
        rows.push(rowObj);
      }
      return { headers, rows };
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = e.target.files?.[0];
    if (!uploadedFile) return;
    setFile(uploadedFile);
    try {
      const { headers, rows } = await parseRowsFromFile(uploadedFile);
      setCsvHeaders(headers);
      setRawRows(rows);

      // Auto-map columns using the fuzzy matching system
      const autoMappings = autoMapColumns(headers, PLAYER_FIELD_NAMES);
      setColumnMappings(autoMappings);
      setStep("mapping");

      toast({ title: translate("player_import_file_processed"), description: `${rows.length} rows detected. Map your columns below.` });
    } catch (error: any) {
      toast({ title: translate("player_import_parse_error"), description: error.message, variant: "destructive" });
    }
  };

  // Apply column mappings to raw rows and produce mapped data + validated players
  const applyMappingsAndPreview = () => {
    const mapped: Record<string, any>[] = [];
    const players: ParsedPlayer[] = [];
    const seenPlayers = new Set<string>();

    for (const row of rawRows) {
      const mappedRow: Record<string, any> = {};
      for (const mapping of columnMappings) {
        if (mapping.mappedTo) {
          mappedRow[mapping.mappedTo] = row[mapping.csvColumn] ?? "";
        }
      }

      const validatedPlayer = validatePlayer(mappedRow);
      const playerKey = `${validatedPlayer.first_name}_${validatedPlayer.last_name}`.toLowerCase();

      if (!seenPlayers.has(playerKey) && (validatedPlayer.first_name || validatedPlayer.last_name)) {
        seenPlayers.add(playerKey);
        players.push(validatedPlayer);
        mapped.push(mappedRow);
      }
    }

    setMappedData(mapped);
    setParsedPlayers(players);
    setStep("preview");
  };

  const handleImport = async () => {
    const validPlayers = parsedPlayers.filter(p => p.valid);
    if (validPlayers.length === 0) {
      toast({ title: translate("player_import_no_valid"), description: translate("player_import_fix_errors"), variant: "destructive" });
      return;
    }

    setIsImporting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;

      // Pre-load all teams for this org so we can resolve squad names to team_ids
      const { data: orgTeams } = await supabase
        .from("teams")
        .select("id, name")
        .eq("organization_id", currentOrganization?.id || "");

      const teamsByName: Record<string, string> = {};
      if (orgTeams) {
        for (const t of orgTeams) {
          teamsByName[t.name.toLowerCase().trim()] = t.id;
        }
      }

      const { data: uploadRecord, error: uploadError } = await supabase
        .from("uploads")
        .insert([{
          user_id: userId!,
          organization_id: currentOrganization?.id,
          domain: "players",
          file_name: file?.name || "manual_import",
          rows_total: validPlayers.length,
          rows_ingested: 0,
          rows_failed: 0,
          status: "pending" as any,
        }])
        .select()
        .single();

      if (uploadError) throw uploadError;

      let importedCount = 0;
      let skippedCount = 0;

      for (const player of validPlayers) {
        // Resolve squad name → team_id (fuzzy: case-insensitive)
        let resolvedTeamId = teamId || null;
        if (player.squad) {
          const squadKey = player.squad.toLowerCase().trim();
          const matchedId = teamsByName[squadKey];
          if (matchedId) {
            resolvedTeamId = matchedId;
          }
          // If no exact match, fall back to the default teamId
        }

        const { data: existingPlayer } = await supabase
          .from("players")
          .select("id")
          .eq("organization_id", currentOrganization?.id || "")
          .eq("first_name", player.first_name)
          .eq("last_name", player.last_name)
          .maybeSingle();

        if (existingPlayer) {
          // Update existing player with any new data
          const updateData: any = {};
          if (player.position) updateData.position = player.position;
          if (player.dominant_foot) updateData.dominant_foot = player.dominant_foot;
          if (player.height_cm) updateData.height_cm = player.height_cm;
          if (player.weight_kg) updateData.weight_kg = player.weight_kg;
          if (player.jersey_number) updateData.jersey_number = player.jersey_number;
          if (player.date_of_birth) updateData.date_of_birth = player.date_of_birth;
          if (player.external_ref) updateData.external_ref = player.external_ref;
          if (resolvedTeamId) updateData.team_id = resolvedTeamId;

          if (Object.keys(updateData).length > 0) {
            await supabase.from("players").update(updateData).eq("id", existingPlayer.id);
          }
          importedCount++;
          continue;
        }

        // Auto-generate a unique external_ref if the user didn't provide one
        const externalRef = player.external_ref && player.external_ref.trim()
          ? player.external_ref.trim()
          : `${player.first_name.substring(0, 2).toUpperCase()}${player.last_name.substring(0, 2).toUpperCase()}${Date.now().toString(36).toUpperCase()}`;

        const { error } = await supabase
          .from("players")
          .insert({
            team_id: resolvedTeamId,
            organization_id: currentOrganization?.id,
            external_ref: externalRef,
            first_name: player.first_name,
            last_name: player.last_name,
            date_of_birth: player.date_of_birth,
            position: player.position,
            dominant_foot: player.dominant_foot,
            height_cm: player.height_cm,
            weight_kg: player.weight_kg,
            jersey_number: player.jersey_number,
            import_batch_id: uploadRecord.id,
          });

        if (error) throw error;
        importedCount++;
      }

      await supabase
        .from("uploads")
        .update({ rows_ingested: importedCount, rows_failed: skippedCount, rows_total: validPlayers.length, status: "completed" as any })
        .eq("id", uploadRecord.id);

      toast({ title: translate("player_import_complete"), description: translate("player_import_result", { imported: importedCount, skipped: skippedCount }) });
      onImportComplete();
      handleClose();
    } catch (error: any) {
      toast({ title: translate("player_import_failed"), description: error.message, variant: "destructive" });
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setParsedPlayers([]);
    setMappedData([]);
    setCsvHeaders([]);
    setRawRows([]);
    setColumnMappings([]);
    setStep("upload");
    onOpenChange(false);
  };

  const validCount = parsedPlayers.filter(p => p.valid).length;
  const invalidCount = parsedPlayers.length - validCount;

  // The system field labels for the preview table
  const previewFields = PLAYER_SCHEMA_FIELDS.filter(f =>
    columnMappings.some(m => m.mappedTo === f.field)
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import Players</DialogTitle>
          <DialogDescription>
            Upload a CSV or Excel (.xlsx) file with your player data
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="csv-upload">Upload CSV or Excel File</Label>
                <Button variant="outline" size="sm" onClick={downloadTemplate}>
                  <Download className="h-4 w-4 mr-2" />
                  Download Template
                </Button>
              </div>
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <Input id="csv-upload" type="file" accept=".csv,.xlsx,.xls" onChange={handleFileUpload} className="max-w-xs mx-auto" />
                <p className="text-sm text-muted-foreground mt-2">CSV or Excel (.xlsx/.xls) file with player information</p>
              </div>
            </div>
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="space-y-1">
                <p><strong>Player names:</strong> Use a single <code className="bg-muted px-1 rounded text-xs">name</code> column (e.g. "John Smith") <em>or</em> separate <code className="bg-muted px-1 rounded text-xs">first_name</code> and <code className="bg-muted px-1 rounded text-xs">last_name</code> columns — both are accepted.</p>
                <p><strong>Squad:</strong> Include a <code className="bg-muted px-1 rounded text-xs">squad</code> column to assign players to the correct team (e.g. "Men's 1st Team"). Must match a team name exactly.</p>
                <p><strong>Unique Player ID:</strong> Optional — leave blank to auto-generate, or provide your own.</p>
              </AlertDescription>
            </Alert>
          </div>
        )}

        {step === "mapping" && (
          <ColumnMappingStep
            csvHeaders={csvHeaders}
            autoMappings={columnMappings}
            schemaFields={PLAYER_SCHEMA_FIELDS}
            onMappingsChange={setColumnMappings}
            onNext={applyMappingsAndPreview}
            onBack={() => setStep("upload")}
          />
        )}

        {step === "preview" && (
          <EditablePlayerPreview
            parsedPlayers={parsedPlayers}
            mappedData={mappedData}
            previewFields={previewFields}
            validCount={validCount}
            invalidCount={invalidCount}
            isImporting={isImporting}
            onDataChange={(index, field, value) => {
              const newMapped = [...mappedData];
              newMapped[index] = { ...newMapped[index], [field]: value };
              setMappedData(newMapped);
              const revalidated = validatePlayer(newMapped[index]);
              const newParsed = [...parsedPlayers];
              newParsed[index] = revalidated;
              setParsedPlayers(newParsed);
            }}
            onCancel={handleClose}
            onBack={() => setStep("mapping")}
            onImport={handleImport}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

// --- Editable preview sub-component ---

import type { SchemaField } from "@/lib/columnMapping";

function EditablePlayerPreview({
  parsedPlayers,
  mappedData,
  previewFields,
  validCount,
  invalidCount,
  isImporting,
  onDataChange,
  onCancel,
  onBack,
  onImport,
}: {
  parsedPlayers: ParsedPlayer[];
  mappedData: Record<string, any>[];
  previewFields: SchemaField[];
  validCount: number;
  invalidCount: number;
  isImporting: boolean;
  onDataChange: (index: number, field: string, value: string) => void;
  onCancel: () => void;
  onBack: () => void;
  onImport: () => void;
}) {
  const [editingCell, setEditingCell] = useState<{ row: number; col: string } | null>(null);
  const [editValue, setEditValue] = useState("");

  const startEditing = (rowIndex: number, col: string, currentValue: any) => {
    setEditingCell({ row: rowIndex, col });
    setEditValue(currentValue?.toString() || "");
  };

  const commitEdit = () => {
    if (editingCell) {
      onDataChange(editingCell.row, editingCell.col, editValue);
    }
    setEditingCell(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h3 className="font-semibold">Preview Import</h3>
          <p className="text-sm text-muted-foreground">
            {validCount} valid, {invalidCount} invalid — click any cell to edit
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onCancel}>
          <X className="h-4 w-4 mr-2" />
          Cancel
        </Button>
      </div>

      <Alert>
        <Pencil className="h-4 w-4" />
        <AlertDescription>
          Click any cell to edit its value before importing. Press Enter to confirm or Escape to cancel.
        </AlertDescription>
      </Alert>

      {invalidCount > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {invalidCount} player{invalidCount !== 1 ? "s have" : " has"} validation errors. Fix them inline or only valid players will be imported.
          </AlertDescription>
        </Alert>
      )}

      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">Status</TableHead>
              {previewFields.map(f => (
                <TableHead key={f.field}>{f.label}</TableHead>
              ))}
              <TableHead>Validation</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {parsedPlayers.map((player, index) => (
              <TableRow key={index} className={!player.valid ? "bg-destructive/10" : ""}>
                <TableCell>
                  {player.valid ? (
                    <CheckCircle2 className="h-4 w-4 text-secondary" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-destructive" />
                  )}
                </TableCell>
                {previewFields.map(f => {
                  const isEditing = editingCell?.row === index && editingCell?.col === f.field;
                  return (
                    <TableCell
                      key={f.field}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => !isEditing && startEditing(index, f.field, mappedData[index]?.[f.field])}
                    >
                      {isEditing ? (
                        <Input
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          onBlur={commitEdit}
                          onKeyDown={e => {
                            if (e.key === "Enter") commitEdit();
                            else if (e.key === "Escape") setEditingCell(null);
                          }}
                          autoFocus
                          className="h-7 text-sm min-w-[80px]"
                        />
                      ) : (
                        mappedData[index]?.[f.field] || "-"
                      )}
                    </TableCell>
                  );
                })}
                <TableCell>
                  {player.errors.length > 0 ? (
                    <span className="text-xs text-destructive">{player.errors.join(", ")}</span>
                  ) : (
                    <span className="text-xs text-muted-foreground">Valid</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex justify-between gap-3">
        <Button variant="outline" onClick={onBack}>
          Back to Mapping
        </Button>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={onImport} disabled={validCount === 0 || isImporting}>
            {isImporting ? "Importing..." : `Import ${validCount} Player${validCount !== 1 ? "s" : ""}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
